import { SVG_SIZE_L, SVG_SIZE_M } from 'src/constants/sizes';
import { Check, ListBullets, PlugsConnected } from '@phosphor-icons/react';
import { WelcomeUpsellModalContentV2 } from 'src/types';
import { Icon, IconName } from 'src/components/Icon';
import { SUPER_PROJECT_NAME } from 'src/constants/global';

const aiModelsIcons: IconName[] = [
  'ninjaOutlined',
  'chatGPTLogo',
  'googleGeminiProLogo',
  'claudeLogo',
  'deepSeekLogo',
  'bedrockStableLogo',
  'metaLogoJSX',
  'nova',
  'alibabaLogoJSX',
  'xaiLogoJSX',
];

const multipleModels = (count: string) => (
  <div className="nj-feature-column">
    <span>Access {count} AI models to suit your project needs.</span>
    <div className="nj-feature-models-list">
      {aiModelsIcons.map((iconType) => (
        <Icon key={iconType} type={iconType} size={SVG_SIZE_M} />
      ))}
    </div>
  </div>
);

export const WELCOME_MODALS_DATA_V2: WelcomeUpsellModalContentV2 = {
  'free-tier-welcome-modal': {
    title: 'Welcome to Ninja!',
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'free-plan-welcome-upgrade',
    features: [
      {
        icon: <ListBullets size={SVG_SIZE_L} />,
        title: '5 daily Autonomous AI tasks to experience the future of work',
        description:
          'Each day you get 5 tasks to transform ideas into finished deliverables.',
      },
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('40+'),
      },
    ],
    couldUpdate: true,
  },
  'pro-10-welcome-modal': {
    title: 'Welcome to Ninja Standard!',
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'pro-basic-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('35+'),
      },
    ],
  },
  'pro-30-welcome-modal': {
    title: 'Welcome to Ninja Ultra!',
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'pro-advanced-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('35+'),
      },
    ],
  },
  'pro-trial-welcome-modal': {
    title: 'Welcome to Ninja Pro Trial!',
    description: `Experience unlimited AI & ${SUPER_PROJECT_NAME} preview for 30 days!`,
    upgradeButtonId: 'pro-trial-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('40+'),
      },
    ],
    couldUpdate: true,
  },
  'max-05-welcome-modal': {
    title: `Welcome to Standard!`,
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'max-05-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('35+'),
      },
    ],
    isMax: true,
  },
  'max-10-welcome-modal': {
    title: `Welcome to Pro!`,
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'max-25-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('35+'),
      },
    ],
    isMax: true,
  },
  'max-20-welcome-modal': {
    title: `Welcome to Ultra!`,
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'max-50-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('40+'),
      },
    ],
    isMax: true,
  },
  'max-30-welcome-modal': {
    title: `Welcome to Max!`,
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'max-50-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('All available'),
      },
    ],
    isMax: true,
  },
  'max-trial-welcome-modal': {
    title: `Welcome to ${SUPER_PROJECT_NAME} Trial!`,
    description: `Experience unlimited AI & ${SUPER_PROJECT_NAME} for 30 days!`,
    upgradeButtonId: 'max-trial-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('All available'),
      },
    ],
    couldUpdate: true,
    isMax: true,
  },
  'business-10-welcome-modal': {
    title: 'Welcome to Business Pro!',
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'business-35-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('35+'),
      },
    ],
    isMax: true,
  },
  'business-20-welcome-modal': {
    title: 'Welcome to Business Ultra!',
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'business-35-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('40+'),
      },
    ],
    isMax: true,
  },
  'business-30-welcome-modal': {
    title: 'Welcome to Business Max!',
    description: "You're now part of the AI revolution!",
    upgradeButtonId: 'business-35-plan-welcome-upgrade',
    features: [
      {
        icon: <PlugsConnected size={SVG_SIZE_L} />,
        title: '15+ specialized AI agents at your fingertips',
        description:
          'Access specialized Writer, Coder, Deep Researcher, Image, Podcast, and File Analysis agents to handle everything from crafting polished content to developing code and generating stunning images.',
      },
      {
        icon: <Check size={SVG_SIZE_L} />,
        title: 'Multiple text and Image AI models available',
        description: multipleModels('All available'),
      },
    ],
    isMax: true,
  },
};
